/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { ModalGeneric, SelectGeneric, TextArea } from 'ypf-pas-general-ui-components';
import { useFormik } from 'formik';

import { capitalize, formatDateToSpanish } from '../../../utils/formatUtils';
import { getTypifications, patchExpertReview } from '../../../services/workReport';
import '../WorkReportModals.scss';
import './ExpertReviewModal.scss';
import useToast from '../../../hooks/useToast';
import LoaderContext from '../../../context/LoaderProvider';

const validationSchema = Yup.object().shape({
  typification: Yup.object().required(),
  comment: Yup.string().required()
});

const ExpertReviewModal = ({
  show,
  onHide,
  onAccept,
  dailyWorkReportId,
  positionName,
  lineName,
  um,
  quantity,
  date,
  assignmentTypeEnum
}) => {
  const { day, month, year } = formatDateToSpanish(date);
  const [disableButton, setDisableButton] = useState(false);
  const [typifications, setTypifications] = useState([]);
  const { setNewToast } = useToast();
  const { setLoader } = useContext(LoaderContext);

  const { values, touched, errors, setFieldValue, setFieldTouched, resetForm } = useFormik({
    initialValues: {
      typification: null,
      comment: ''
    },
    validationSchema
  });

  useEffect(() => {
    if (!!Object.values(errors).length || !Object.values(touched).length) {
      setDisableButton(true);
    } else {
      setDisableButton(false);
    }
  }, [errors, touched]);

  useEffect(() => {
    if (!show) resetForm();
    else getTypifications().then((data) => setTypifications(data));
  }, [show]);

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={onHide}
      primaryAction={async () => {
        try {
          setLoader(true);
          setDisableButton(true);
          const response = await patchExpertReview(dailyWorkReportId, assignmentTypeEnum, {
            tipificacionEnum: values?.typification?.id,
            motivoRevision: values?.comment
          });

          onHide();
          setNewToast({
            type: 'SUCCESS',
            message: response?.mensaje || response
          });
          onAccept();
        } catch (err) {
          setNewToast({
            type: 'ERROR',
            message: err.message
          });
        } finally {
          setLoader(false);
          setDisableButton(false);
        }
      }}
      primaryActionDisabled={disableButton}
      primaryActionLabel="Comunicar"
      secondaryAction={onHide}
      secondaryActionLabel="Salir"
      show={show}
      size="xl"
      title="Requiere revisión experta"
    >
      <>
        <div className="text-center modal-info">
          <div className="d-flex justify-content-center flex-wrap">
            <span>&quot;{positionName}</span>
            <span>{lineName}</span>
            <span>
              {um} {quantity}&quot;
            </span>
          </div>
          Para el día {capitalize(day?.text)}, {day?.number} de {month} de {year}
        </div>
        <hr className="my-4" />
        <div className="w-50">
          <SelectGeneric
            className="typification-select"
            label="Tipificación"
            name="typification"
            onChange={async (val) => {
              await setFieldValue('typification', val);
              setFieldTouched('typification');
            }}
            options={typifications}
            placeholder="Seleccione una tipificación"
            value={values?.typification}
            withSelect="100%"
          />
        </div>
        <div className="comment-input-container">
          <TextArea
            label="Comentario"
            maxLength={512}
            name="comment"
            onChange={async (e) => {
              await setFieldValue('comment', e.target.value);
              setFieldTouched('comment');
            }}
            placeholder="Motivo de revisión experta"
            resize={false}
            value={values?.comment}
          />
        </div>
        <hr className="divide-line-bottom" />
      </>
    </ModalGeneric>
  );
};

export default ExpertReviewModal;

ExpertReviewModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onAccept: PropTypes.func,
  dailyWorkReportId: PropTypes.number,
  positionName: PropTypes.string,
  lineName: PropTypes.string,
  um: PropTypes.string,
  quantity: PropTypes.number,
  date: PropTypes.string,
  assignmentTypeEnum: PropTypes.shape({
    id: PropTypes.number,
    nombre: PropTypes.string
  })
};

ExpertReviewModal.defaultProps = {
  show: false,
  onHide: () => {
    /* Empty */
  },
  onAccept: () => {
    /* Empty */
  },
  dailyWorkReportId: null,
  positionName: '',
  lineName: '',
  um: '',
  quantity: 0,
  date: '',
  assignmentTypeEnum: {
    id: 0,
    nombre: ''
  }
};
